import React, { type ReactElement, memo } from 'react';
import {
	BADGE_INNER_POSITION as INNER,
	BADGE_OUTER_POSITION as OUTER,
} from '@atlassian/jira-business-collaboration/src/ui/node-selection/constants.tsx';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import {
	isTopLevelField,
	getTopLevelFieldKey,
} from '@atlassian/jira-business-fields/src/common/supported-field-types/index.tsx';
import type {
	FieldCellProps,
	ListIssueFieldTransformed,
	TopLevelFieldTransformed,
	IssueUpdatedHandler,
} from '@atlassian/jira-business-fields/src/common/types.tsx';
import {
	FieldCell,
	isFieldCellSupported,
	isFieldCellTypes,
} from '@atlassian/jira-business-fields/src/ui/cell.tsx';
import { loadingCellStyles } from '../../../common/ui/loading-cell/index.tsx';
import { isItemSkeleton } from '../../../common/utils/row-placeholder/index.tsx';
import { PLACEHOLDER_COLUMN } from '../../../constants.tsx';
import { useIsColumnVisible } from '../../../controllers/column-visibility/index.tsx';
import type { ListViewControllerData } from '../../../controllers/list-view-issues/types.tsx';
import { WithCellSelection } from './with-cell-selection/index.tsx';

export type CellRendererParamType = FieldCellProps<ListViewControllerData, unknown>;

export type CellProps = {
	cellRenderers: Record<string, (params: CellRendererParamType) => ReactElement>;
	field: BusinessIssueField;
	onIssueUpdated: IssueUpdatedHandler;
	projectId: string;
	width: number;
} & CellRendererParamType;

export const Cell = memo<CellProps>(
	({ cellRenderers, field, onIssueUpdated, projectId, width, ...rest }) => {
		const cellRenderer =
			isFieldCellTypes(field.type) && isFieldCellSupported(field.type)
				? (params: CellRendererParamType) => {
						const { rowData, rowIndex, columnIndex } = params;
						if (isItemSkeleton(rowData.issueId) || field.id.startsWith(PLACEHOLDER_COLUMN)) {
							// experimenting with inlining CSS here to avoid styled-components runtime perf overhead
							return (
								<div
									data-testId="business-list.ui.list-view.loading-cell"
									// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									style={loadingCellStyles}
								/>
							);
						}

						let cellData: ListIssueFieldTransformed | TopLevelFieldTransformed | undefined | null;

						if (isTopLevelField(field.type)) {
							const key = getTopLevelFieldKey(field.type);
							if (key) {
								cellData = rowData[key];
							}
						} else {
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							cellData = rowData.fields?.find(
								(f) => f.fieldId === field.id,
							) as ListIssueFieldTransformed;
						}
						return (
							<WithCellSelection
								depth={rowData.depth}
								width={width}
								fieldId={field.id}
								projectId={projectId}
								issueId={rowData.issueId}
								position={rowIndex === 0 ? INNER : OUTER}
								rowIndex={rowIndex}
								columnIndex={columnIndex}
								field={field}
								issue={rowData}
							>
								{() => (
									// Check for type existence was already done above
									<FieldCell
										fieldType={field.type ?? ''}
										{...params}
										cellData={cellData}
										onIssueUpdated={onIssueUpdated}
										fieldId={field.id}
										fieldName={field.name}
									/>
								)}
							</WithCellSelection>
						);
					}
				: (params: CellRendererParamType) => {
						const { rowData, rowIndex, columnIndex } = params;
						if (isItemSkeleton(rowData.issueId) || field.id.startsWith(PLACEHOLDER_COLUMN)) {
							// experimenting with inlining CSS here to avoid styled-components runtime perf overhead
							return (
								<div
									data-testId="business-list.ui.list-view.loading-cell"
									// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									style={loadingCellStyles}
								/>
							);
						}

						return (
							<WithCellSelection
								depth={rowData.depth}
								width={width}
								fieldId={field.id}
								projectId={projectId}
								issueId={rowData.issueId}
								position={rowIndex === 0 ? INNER : OUTER}
								rowIndex={rowIndex}
								columnIndex={columnIndex}
								field={field}
								issue={rowData}
							>
								{() => cellRenderers[field.id](params)}
							</WithCellSelection>
						);
					};

		return (
			<>
				{cellRenderer({
					...rest,
					onIssueUpdated,
				})}
			</>
		);
	},
);

Cell.displayName = 'Cell';

export const CellWithHorizontalVirtualization = (props: CellProps) => {
	const isColumnVisible = useIsColumnVisible(props.column);

	return isColumnVisible ? <Cell {...props} /> : null;
};
